'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { cn } from '@/shared/lib/cn';
import type { BuildingBrief, BuildingBriefQuestion } from '@/types/project';

interface BuildingBriefQuestionsProps {
  brief: BuildingBrief;
  disabled?: boolean;
  onAnswer: (key: string, value: string | number) => void;
}

/**
 * The short list of gaps left before a building with no photos can be drawn.
 *
 * Three product rules are visible here, and each one keeps this from becoming a
 * CAD form:
 *  - only questions that change the picture are ever asked (the server decides);
 *  - a question that a drawing already answered never appears;
 *  - nothing blocks — the counter shows progress, it does not gate the design.
 *
 * The question text, the reason it matters and the options are all server-
 * composed (API-driven messages) — this component never writes copy of its own.
 */
export function BuildingBriefQuestions({
  brief,
  disabled,
  onAnswer,
}: BuildingBriefQuestionsProps) {
  const t = useTranslations('project');

  if (!brief.is_applicable || brief.questions.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <Label>{t('briefQuestionsTitle')}</Label>
        <span className="text-form-help text-muted-foreground">
          {t('briefQuestionsProgress', {
            answered: brief.answered,
            total: brief.total,
          })}
        </span>
      </div>

      <div className="space-y-3">
        {brief.questions.map((question) => (
          <QuestionCard
            key={question.key}
            question={question}
            disabled={disabled}
            onAnswer={onAnswer}
          />
        ))}
      </div>
    </div>
  );
}

function QuestionCard({
  question,
  disabled,
  onAnswer,
}: {
  question: BuildingBriefQuestion;
  disabled?: boolean;
  onAnswer: (key: string, value: string | number) => void;
}) {
  const [numberValue, setNumberValue] = useState('');

  return (
    <div className="border-border rounded-lg border p-3">
      <p className="text-form-label font-medium">{question.question}</p>
      {/* Why it matters. A question whose point is visible gets answered. */}
      <p className="text-form-help text-muted-foreground mt-1">
        {question.impact}
      </p>

      {question.input_type === 'choice' && (
        <div className="mt-3 flex flex-wrap gap-2">
          {(question.options ?? []).map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => onAnswer(question.key, option.value)}
              className={cn(
                'border-border rounded-full border px-3 py-1.5',
                'text-form-help transition-colors',
                'hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-60'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {question.input_type === 'number' && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {(question.suggestions ?? []).map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              disabled={disabled}
              onClick={() => onAnswer(question.key, suggestion)}
              className={cn(
                'border-border rounded-full border px-3 py-1.5',
                'text-form-help transition-colors',
                'hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-60'
              )}
            >
              {suggestion}
            </button>
          ))}
          <Input
            type="number"
            inputMode="numeric"
            min={question.min}
            max={question.max}
            value={numberValue}
            disabled={disabled}
            onChange={(e) => setNumberValue(e.target.value)}
            onBlur={() => {
              const parsed = Number(numberValue);
              if (!numberValue || Number.isNaN(parsed)) return;
              onAnswer(question.key, parsed);
            }}
            className="w-24"
          />
        </div>
      )}
    </div>
  );
}
